import { supabaseAdmin } from '@/lib/supabase';
import { differenceInDays } from 'date-fns';

// Safe date to ISO string conversion with validation (module-level function)
const safeToISOString = (date: Date | null | undefined, context: string = 'unknown'): string | null => {
  if (!date) return null;
  if (!(date instanceof Date)) {
    console.error(`❌ ${context}: Not a Date object:`, date);
    throw new Error(`${context}: Expected Date object but got ${typeof date}`);
  }
  if (isNaN(date.getTime())) {
    console.error(`❌ ${context}: Invalid Date object:`, date);
    throw new Error(`${context}: Cannot convert invalid Date to ISO string`);
  }
  return date.toISOString();
};

// Helper function to set cycle end date to 11:59:59 PM of the same day
// User selects the actual end date (e.g., "6" means cycle ends on 6th at 23:59:59)
// This function takes a date at 00:00:00 and adjusts it to 23:59:59 of the SAME day
const adjustCycleEndToEndOfDay = (date: Date): Date => {
  const adjusted = new Date(date);
  // Set to end of day (23:59:59.999) without changing the date
  adjusted.setUTCHours(23, 59, 59, 999);
  return adjusted;
};

// Helper function to detect Capital One cards based on institution and card names
function isCapitalOneCard(institutionName?: string, cardName?: string): boolean {
  const capitalOneIndicators = ['capital one', 'quicksilver', 'venture', 'savor', 'spark'];
  const institutionMatch = institutionName?.toLowerCase().includes('capital one') || false;
  const cardMatch = capitalOneIndicators.some(indicator => 
    cardName?.toLowerCase().includes(indicator)
  ) || false;
  
  return institutionMatch || cardMatch;
}

// Helper function to detect Robinhood cards
function isRobinhoodCard(institutionId?: string, institutionName?: string): boolean {
  return institutionId === 'ins_54' || /robinhood/i.test(institutionName || '');
}

// Helper function to get the effective transaction date based on institution
function getEffectiveTransactionDate(transaction: any, institutionId?: string, institutionName?: string): Date {
  const isRobinhood = isRobinhoodCard(institutionId, institutionName);

  if (isRobinhood) {
    // For Robinhood, always use posted date (transaction.date) to align with billing cycles
    return transaction.date;
  } else {
    // For other institutions, use posted date as well (transaction.date is the standard)
    // Could potentially use authorized_date for different behavior if needed in the future
    return transaction.date;
  }
}

// Manual anchor scheduling constants tuned for American Express-like variability
const MANUAL_TARGET_CYCLE_LENGTH = 30.4375; // Average days per month over a year
const MANUAL_DAY_OFFSETS = [0, -1, -2, -3];
const DAY_IN_MS = 24 * 60 * 60 * 1000;

interface ManualAnchorScheduleOptions {
  anchorEnd: Date;
  anchorDay: number;
  cyclesToGenerate: number;
}

interface ManualAnchorScheduleResult {
  boundaries: Date[];
  finalDrift: number;
}

interface ManualForwardEndOptions {
  anchorEnd: Date;
  anchorDay: number;
  drift: number;
}

const toUtcDate = (value: Date): Date => {
  return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
};

const isWeekend = (date: Date): boolean => {
  const day = date.getUTCDay();
  return day === 0 || day === 6;
};

type ManualCandidate = {
  end: Date;
  newDrift: number;
  cycleLength: number;
  offset: number;
  day: number;
  score: number;
};

const getManualPriority = (daysInMonth: number): Record<number, number> => {
  if (daysInMonth <= 29) {
    return { 0: 30, '-1': 15, '-2': 5, '-3': 0 } as Record<number, number>;
  }
  if (daysInMonth === 31) {
    return { 0: 12, '-1': 0, '-2': 6, '-3': 9 } as Record<number, number>;
  }
  // 30-day months
  return { 0: 0, '-1': 6, '-2': 10, '-3': 14 } as Record<number, number>;
};

const buildManualCandidates = (
  currentEnd: Date,
  anchorDay: number,
  drift: number,
  direction: 'backward' | 'forward'
): ManualCandidate[] => {
  const referenceDate = direction === 'backward'
    ? new Date(Date.UTC(currentEnd.getUTCFullYear(), currentEnd.getUTCMonth(), 1))
    : new Date(Date.UTC(currentEnd.getUTCFullYear(), currentEnd.getUTCMonth() + 2, 1));

  if (direction === 'backward') {
    referenceDate.setUTCMonth(referenceDate.getUTCMonth() - 1);
  } else {
    referenceDate.setUTCMonth(referenceDate.getUTCMonth() - 1);
  }

  const year = referenceDate.getUTCFullYear();
  const month = referenceDate.getUTCMonth();
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const basePriority = getManualPriority(daysInMonth);

  if (daysInMonth === 31) {
    if (drift > 0.5) {
      basePriority['-1'] = (basePriority['-1'] ?? 0) - 8;
      basePriority['0'] = (basePriority['0'] ?? 0) + 6;
    }
    if (drift < -0.5) {
      basePriority['0'] = (basePriority['0'] ?? 0) - 12;
      basePriority['-1'] = (basePriority['-1'] ?? 0) + 6;
    }
  }

  if (daysInMonth === 30) {
    if (drift > 0.75) {
      basePriority['-1'] = (basePriority['-1'] ?? 0) - 4;
    }
    if (drift < -0.75) {
      basePriority['0'] = (basePriority['0'] ?? 0) - 4;
    }
  }

  if (daysInMonth <= 29) {
    basePriority['-3'] = (basePriority['-3'] ?? 0) - 25;
    basePriority['-2'] = (basePriority['-2'] ?? 0) - 5;
    basePriority['0'] = (basePriority['0'] ?? 0) + 12;
  }

  return MANUAL_DAY_OFFSETS.map(offset => {
    const candidateDayRaw = anchorDay + offset;
    if (candidateDayRaw < 1) {
      return null;
    }
    const day = Math.min(candidateDayRaw, daysInMonth);
    const candidateEnd = direction === 'backward'
      ? new Date(Date.UTC(year, month, day))
      : new Date(Date.UTC(year, month, day));

    const cycleLength = direction === 'backward'
      ? Math.round((currentEnd.getTime() - candidateEnd.getTime()) / DAY_IN_MS)
      : Math.round((candidateEnd.getTime() - currentEnd.getTime()) / DAY_IN_MS);

    if (cycleLength < 28 || cycleLength > 32) {
      return null;
    }

    const lengthDiff = cycleLength - MANUAL_TARGET_CYCLE_LENGTH;
    const newDrift = drift + lengthDiff * (direction === 'backward' ? 1 : 1);
    const startDate = direction === 'backward'
      ? new Date(candidateEnd.getTime() + DAY_IN_MS)
      : new Date(currentEnd.getTime() + DAY_IN_MS);
    const dueDate = new Date(candidateEnd.getTime() + 25 * DAY_IN_MS);

    let score = 0;
    const dueDay = dueDate.getUTCDate();
    score += Math.abs(dueDay - 13) * 35;
    score += Math.abs(lengthDiff) * 18;
    score += Math.abs(newDrift) * 6;
    score += Math.abs((startDate.getUTCDate()) - anchorDay) * 4;
    score += Math.abs(offset) * 3;

    const dayOfWeek = candidateEnd.getUTCDay();
    if (dayOfWeek === 6) {
      score += 45; // Saturday close strongly discouraged
    } else if (dayOfWeek === 0) {
      score += 25; // Sunday close discouraged but allowed
    }

    if (isWeekend(startDate)) {
      score += 10;
    }

    if (daysInMonth === 31 && offset === -1) {
      score -= 8;
    }

    if (daysInMonth <= 29 && offset === -3) {
      score -= 12;
    }

    score += (basePriority[offset as keyof typeof basePriority] ?? 20);

    return {
      end: candidateEnd,
      newDrift,
      cycleLength,
      offset,
      day,
      score,
    };
  }).filter((candidate): candidate is ManualCandidate => candidate !== null);
};

const pickManualBoundary = (
  currentEnd: Date,
  anchorDay: number,
  drift: number
): { nextEnd: Date; newDrift: number } => {
  const candidates = buildManualCandidates(currentEnd, anchorDay, drift, 'backward');
  if (candidates.length === 0) {
    const prevMonthStart = new Date(Date.UTC(currentEnd.getUTCFullYear(), currentEnd.getUTCMonth(), 1));
    prevMonthStart.setUTCMonth(prevMonthStart.getUTCMonth() - 1);
    const year = prevMonthStart.getUTCFullYear();
    const month = prevMonthStart.getUTCMonth();
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const fallbackDay = Math.min(Math.max(anchorDay, 1), daysInMonth);
    const fallbackEnd = new Date(Date.UTC(year, month, fallbackDay));
    const length = Math.round((currentEnd.getTime() - fallbackEnd.getTime()) / DAY_IN_MS);
    const newDrift = drift + (length - MANUAL_TARGET_CYCLE_LENGTH);
    return { nextEnd: fallbackEnd, newDrift };
  }

  candidates.sort((a, b) => {
    if (a.score !== b.score) {
      return a.score - b.score;
    }
    return b.day - a.day;
  });

  return {
    nextEnd: candidates[0].end,
    newDrift: candidates[0].newDrift,
  };
};

const generateManualAnchorSchedule = (
  options: ManualAnchorScheduleOptions
): ManualAnchorScheduleResult => {
  const boundaries: Date[] = [];
  let currentEnd = toUtcDate(options.anchorEnd);
  let drift = 0;

  boundaries.push(currentEnd);

  for (let i = 0; i < options.cyclesToGenerate; i++) {
    const { nextEnd, newDrift } = pickManualBoundary(currentEnd, options.anchorDay, drift);
    currentEnd = nextEnd;
    drift = newDrift;
    boundaries.push(currentEnd);
  }

  return { boundaries, finalDrift: drift };
};

const pickManualForwardEnd = (
  options: ManualForwardEndOptions
): Date => {
  const candidates = buildManualCandidates(options.anchorEnd, options.anchorDay, options.drift, 'forward');

  if (candidates.length === 0) {
    const nextMonthStart = new Date(Date.UTC(options.anchorEnd.getUTCFullYear(), options.anchorEnd.getUTCMonth() + 1, 1));
    const year = nextMonthStart.getUTCFullYear();
    const month = nextMonthStart.getUTCMonth();
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const fallbackDay = Math.min(Math.max(options.anchorDay, 1), daysInMonth);
    return new Date(Date.UTC(year, month, fallbackDay));
  }

  candidates.sort((a, b) => {
    if (a.score !== b.score) {
      return a.score - b.score;
    }
    return b.day - a.day;
  });

  return candidates[0].end;
};

// Helper function to identify payment transactions based on transaction name
export function isPaymentTransaction(transactionName: string): boolean {
  if (!transactionName) return false;
  
  const lowerName = transactionName.toLowerCase();
  
  // Common payment indicators across different banks
  const paymentIndicators = [
    'pymt',           // Capital One payments
    'payment',        // Amex and other banks (covers "Online Ach Payment Ref")
    'autopay',        // Automatic payments
    'mobile pymt',    // Mobile payments
    'web pymt',       // Web payments
    'transfer from', // Bank transfers from checking/savings
    'transfer to checking', // Transfers to checking
    'transfer to savings', // Transfers to savings
    'credit card pymt', // Credit card payments
    'cc pymt',        // Credit card payment abbreviation
    'bill pay',       // Bill payment
    'scheduled pymt', // Scheduled payments
    'recurring pymt', // Recurring payments
    'automatic debit', // Automatic debits
    'direct debit',   // Direct debits
    'e-payment',      // Electronic payments
    'epayment',       // Electronic payments (no dash)
    'online pymt',    // Online payments
    'phone pymt',     // Phone payments
    'bank pymt',      // Bank payments
    'ach credit',     // ACH credits (incoming payments to card)
    'ach debit',      // ACH debits
    'wire transfer',  // Wire transfers
    'balance transfer', // Balance transfers (these reduce balance like payments)
  ];
  
  // Check for any payment indicator
  const hasPaymentIndicator = paymentIndicators.some(indicator => lowerName.includes(indicator));
  
  // Additional checks for specific patterns that might be missed
  // Check for "payment" or "pymt" anywhere in the string (already covered above)
  // Check for "transfer" but exclude "balance transfer fee" or similar fee transactions
  const isTransferPayment = lowerName.includes('transfer') && 
                           !lowerName.includes('fee') && 
                           !lowerName.includes('charge');
  
  return hasPaymentIndicator || isTransferPayment;
}

export interface BillingCycleData {
  id: string;
  creditCardId: string;
  creditCardName: string;
  creditCardMask?: string;
  startDate: Date;
  endDate: Date;
  statementBalance?: number;
  minimumPayment?: number;
  dueDate?: Date;
  totalSpend: number;
  transactioncount: number;
}

export async function calculateBillingCycles(
  creditCardId: string,
  options?: {
    statementPeriods?: { startDate: Date | null; endDate: Date; dueDate?: Date | null }[];
    baselineDueDate?: Date | null;
  }
): Promise<BillingCycleData[]> {
  // Get credit card data
  const { data: creditCard, error: cardError } = await supabaseAdmin
    .from('credit_cards')
    .select('*')
    .eq('id', creditCardId)
    .single();

  if (cardError || !creditCard) {
    throw new Error('Credit card not found');
  }

  // Get ALL transactions for this credit card, ordered by date
  // Supabase PostgREST defaults to 1000 rows max — paginate to ensure
  // high-volume cards (e.g. Robinhood with 100+ txns/month) get complete data.
  let transactions: any[] = [];
  {
    const PAGE_SIZE = 1000;
    let page = 0;
    let hasMore = true;

    while (hasMore) {
      const { data: pageData, error: pageError } = await supabaseAdmin
        .from('transactions')
        .select('*')
        .eq('creditCardId', creditCardId)
        .order('date', { ascending: true })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (pageError) {
        throw new Error('Failed to fetch transactions');
      }

      transactions = transactions.concat(pageData || []);
      hasMore = (pageData?.length || 0) === PAGE_SIZE;
      page++;
    }

    if (transactions.length > 1000) {
      console.log(`📊 Paginated fetch: retrieved ${transactions.length} transactions across ${page} pages for card ${creditCardId}`);
    }
  }

  // Add transactions to creditCard object to maintain compatibility
  const creditCardWithTransactions = {
    ...creditCard,
    transactions: transactions || [],
    // Convert date strings back to Date objects for compatibility
    lastStatementIssueDate: creditCard.lastStatementIssueDate ? new Date(creditCard.lastStatementIssueDate) : null,
    nextPaymentDueDate: creditCard.nextPaymentDueDate ? new Date(creditCard.nextPaymentDueDate) : null,
    openDate: creditCard.openDate ? new Date(creditCard.openDate) : null,
    annualFeeDueDate: creditCard.annualFeeDueDate ? new Date(creditCard.annualFeeDueDate) : null,
  };

  // Convert transaction dates to Date objects for compatibility
  const transactionsWithDates = (transactions || []).map(t => ({
    ...t,
    date: new Date(t.date),
    authorizedDate: t.authorizedDate ? new Date(t.authorizedDate) : null,
  }));

  // Update the credit card object with converted transactions
  creditCardWithTransactions.transactions = transactionsWithDates;

  let cycles: BillingCycleData[] = [];
  
  // PRIORITY ORDER for billing cycle dates:
  // 1. Real Plaid statement data (has_real_plaid_statements = true) - HIGHEST PRIORITY
  // 2. User manual configuration - if no real Plaid statements
  // 3. Prompt user for manual config - if neither exists

  let lastStatementDate = creditCardWithTransactions.lastStatementIssueDate;
  const cycleDateType = (creditCardWithTransactions as any).cycle_date_type;
  const hasRealPlaidStatements = (creditCardWithTransactions as any).has_real_plaid_statements === true;

  // DEBUG: Log card configuration to understand why dynamic anchor might not be triggered
  console.log(`🔍 CARD CONFIGURATION DEBUG for ${creditCardWithTransactions.name}:`, {
    cycle_date_type: cycleDateType,
    manual_cycle_day: creditCardWithTransactions.manual_cycle_day,
    manual_dates_configured: creditCardWithTransactions.manual_dates_configured,
    has_real_plaid_statements: hasRealPlaidStatements,
    lastStatementIssueDate: safeToISOString(lastStatementDate, 'lastStatementIssueDate debug'),
    dynamicAnchorConditionsMet: cycleDateType === 'dynamic_anchor' && creditCardWithTransactions.manual_cycle_day
  });

  if (hasRealPlaidStatements && lastStatementDate) {
    // PRIORITY 1: Use real Plaid statement dates - most accurate
    console.log(`🎯 Using REAL Plaid statement dates for ${creditCardWithTransactions.name} (statements API)`);
    // Keep existing lastStatementDate - it's from real API data
  } else if (creditCardWithTransactions.manual_dates_configured) {
    // PRIORITY 2: Use manual configuration (no real Plaid statements available)
    console.log(`🎯 Using manual date configuration for ${creditCardWithTransactions.name} (no real Plaid statements)`);
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const todayDate = today.getDate();

    // Calculate the cycle end day for current month based on user's manual settings
    let cycleEndDay: number;

    if (cycleDateType === 'same_day' && creditCardWithTransactions.manual_cycle_day) {
      cycleEndDay = creditCardWithTransactions.manual_cycle_day;
    } else if (cycleDateType === 'days_before_end' && creditCardWithTransactions.cycle_days_before_end) {
      const daysInCurrentMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
      cycleEndDay = daysInCurrentMonth - creditCardWithTransactions.cycle_days_before_end;
    } else if (cycleDateType === 'dynamic_anchor' && creditCardWithTransactions.manual_cycle_day) {
      const daysInCurrentMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
      cycleEndDay = Math.min(creditCardWithTransactions.manual_cycle_day, daysInCurrentMonth);
    } else {
      // Manual config marked but incomplete - need user to finish configuration
      console.warn(`⚠️ Manual configuration incomplete for ${creditCardWithTransactions.name} - user needs to complete setup`);
      return cycles; // Return empty cycles to prompt user for configuration
    }
    
    // If today > cycle end day, then current month's cycle has closed
    // A cycle ending on the 6th is still open on the 6th (closes at 11:59:59 PM)
    // Otherwise, last month's cycle is the most recent closed
    let targetMonth: number, targetYear: number;
    if (todayDate > cycleEndDay) {
      // Current month's cycle has closed
      targetMonth = currentMonth;
      targetYear = currentYear;
    } else {
      // Last month's cycle is most recent closed (or current cycle is still open on its end day)
      targetMonth = currentMonth - 1;
      targetYear = currentMonth === 0 ? currentYear - 1 : currentYear; // Handle January -> December
    }
    
    if (!lastStatementDate) {
      // Calculate the actual end date for the target month using manual settings only when we lack liabilities data
      let manualCycleEndDay = cycleEndDay;
      if (cycleDateType === 'days_before_end' && creditCardWithTransactions.cycle_days_before_end) {
        const daysInTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
        manualCycleEndDay = daysInTargetMonth - creditCardWithTransactions.cycle_days_before_end;
      } else if (cycleDateType === 'dynamic_anchor' && creditCardWithTransactions.manual_cycle_day) {
        const daysInTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
        manualCycleEndDay = Math.min(creditCardWithTransactions.manual_cycle_day, daysInTargetMonth);
      }

      lastStatementDate = new Date(Date.UTC(targetYear, targetMonth, manualCycleEndDay));

      console.log('🔧 Using manual date as anchor for billing cycles:', {
        cycleDateType,
        cycleEndDay,
        targetMonth: targetMonth + 1, // Display as 1-12
        targetYear,
        manualCycleEndDay,
        calculatedAnchor: lastStatementDate.toDateString(),
        originalPlaidDate: creditCardWithTransactions.lastStatementIssueDate?.toDateString()
      });
    } else {
      console.log('🔒 Retaining liabilities statement anchor for billing cycles:', {
        cycleDateType,
        cycleEndDay,
        targetMonth: targetMonth + 1, // Display as 1-12
        targetYear,
        liabilitiesAnchor: lastStatementDate.toDateString(),
        manualAnchorTarget: creditCardWithTransactions.manual_cycle_day
      });
    }
  }
  const nextDueDate = creditCardWithTransactions.nextPaymentDueDate;

  // If explicit statement periods are provided, build cycles strictly from them
  if (options?.statementPeriods && options.statementPeriods.length > 0) {
    const provided = [...options.statementPeriods]
      .filter(p => p.endDate instanceof Date && !isNaN(p.endDate.getTime()))
      .sort((a, b) => b.endDate.getTime() - a.endDate.getTime());

    const baselineDue = options.baselineDueDate || nextDueDate || null;
    const baselineDay = baselineDue ? new Date(baselineDue).getDate() : null;

    // Helper to estimate historical due date as same day-of-month as baseline due date
    const estimateHistoricalDue = (cycleEnd: Date): Date | null => {
      if (!baselineDay) return null;
      const m = cycleEnd.getUTCMonth() + 1; // next month
      const y = cycleEnd.getUTCFullYear() + (m > 11 ? 1 : 0);
      const nextMonth = (m % 12);
      const daysInMonth = new Date(Date.UTC(y, nextMonth + 1, 0)).getUTCDate();
      const day = Math.min(baselineDay, daysInMonth);
      return new Date(Date.UTC(y, nextMonth, day));
    };

    // Create cycles for each statement period (historical closed cycles)
    for (let i = 0; i < provided.length; i++) {
      const period = provided[i];
      // Skip if startDate is missing (cannot create a proper window) and there is no older statement to infer
      if (!period.startDate) {
        // If we have the next older period, it would have given us a start; skip this one
        continue;
      }
      const isMostRecentClosed = lastStatementDate && period.endDate.getTime() === lastStatementDate.getTime();
      const due = isMostRecentClosed ? (nextDueDate || null) : (period.dueDate ?? estimateHistoricalDue(period.endDate));
      await createOrUpdateCycle(
        creditCardWithTransactions,
        cycles,
        period.startDate,
        period.endDate,
        due,
        true,
        transactionsWithDates
      );
    }

    // Also include current open cycle if we have a last statement anchor
    if (lastStatementDate) {
      const currentCycleStart = new Date(lastStatementDate);
      currentCycleStart.setUTCDate(currentCycleStart.getUTCDate() + 1);
      // End = same day-of-month as anchor, one month into the future (clamped for month length)
      const anchor = new Date(lastStatementDate);
      const y = anchor.getUTCFullYear();
      const m = anchor.getUTCMonth() + 1; // next month
      const y2 = y + (m > 11 ? 1 : 0);
      const m2 = (m % 12);
      const targetDay = anchor.getUTCDate();
      const dim = new Date(Date.UTC(y2, m2 + 1, 0)).getUTCDate();
      const currentCycleEnd = new Date(Date.UTC(y2, m2, Math.min(targetDay, dim)));
      const currentDue = null; // Do not guess due date for open cycle
      await createOrUpdateCycle(
        creditCardWithTransactions,
        cycles,
        currentCycleStart,
        currentCycleEnd,
        currentDue,
        false,
        transactionsWithDates
      );
    }

    // Filter out cycles prior to adjusted open date (Capital One = earliest transaction; others = true open date)
    try {
      const isCapOneByName = isCapitalOneCard(undefined, creditCardWithTransactions.name);
      const earliestTxn = (transactionsWithDates && transactionsWithDates.length > 0)
        ? new Date(Math.min(...transactionsWithDates.map(t => t.date.getTime())))
        : null;
      const trueOpen = creditCardWithTransactions.openDate ? new Date(creditCardWithTransactions.openDate) : null;
      const adjustedOpen = isCapOneByName && earliestTxn
        ? (trueOpen ? (earliestTxn > trueOpen ? earliestTxn : trueOpen) : earliestTxn)
        : trueOpen;
      if (adjustedOpen) {
        cycles = cycles.filter(c => new Date(c.endDate) >= adjustedOpen);
      }
    } catch {}
    return cycles.sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
  } else if (lastStatementDate) {
    // PRIORITY 3: Show estimated cycles from liabilities API while prompting for manual config
    console.log(`⚠️ Using estimated cycles for ${creditCardWithTransactions.name} (user should configure manual dates)`);
    console.log(`   - has_real_plaid_statements: ${hasRealPlaidStatements}`);
    console.log(`   - manual_dates_configured: ${creditCardWithTransactions.manual_dates_configured}`);
    console.log(`   - Using lastStatementDate: ${lastStatementDate.toDateString()} (from liabilities API)`);
    console.log(`   → Showing estimated cycles, but user should configure manual billing dates for accuracy`);
    // Continue with cycle generation using liabilities API date as anchor
  } else {
    // No billing date source available at all
    console.log(`⚠️ No billing date source available for ${creditCardWithTransactions.name}`);
    console.log(`   → User must configure manual billing dates to see cycles`);
    return cycles.sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
  }

  // Continue with cycle generation using established anchor date
  if (!lastStatementDate) {
    console.log(`⚠️ No statement anchor available for ${creditCardWithTransactions.name} after priority check`);
    return cycles.sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
  }

  // Build cycles based on the anchor's day-of-month (or manual config if present)
  const baselineDue = nextDueDate || null;
  const baselineDay = baselineDue ? new Date(baselineDue).getDate() : null;
  const toUtcMidnight = (value: Date): Date => {
    return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
  };

  const createUtcDate = (year: number, month: number, day: number): Date => {
    // Validate inputs before creating date
    if (!Number.isInteger(year) || year < 1900 || year > 3000) {
      console.error(`❌ Invalid year: ${year}`);
      throw new Error(`Invalid year for date creation: ${year}`);
    }
    if (!Number.isInteger(month) || month < 0 || month > 11) {
      console.error(`❌ Invalid month: ${month} (should be 0-11)`);
      throw new Error(`Invalid month for date creation: ${month}`);
    }
    if (!Number.isInteger(day) || day < 1 || day > 31) {
      console.error(`❌ Invalid day: ${day}`);
      throw new Error(`Invalid day for date creation: ${day}`);
    }

    const date = new Date(Date.UTC(year, month, day));
    if (isNaN(date.getTime())) {
      console.error(`❌ Created invalid date from: year=${year}, month=${month}, day=${day}`);
      throw new Error(`Invalid date created: ${year}-${month+1}-${day}`);
    }
    return date;
  };


  const anchorEnd = toUtcMidnight(new Date(lastStatementDate));
  // Get baseline closing day for same_day type (days_before_end is calculated per month)
  let baselineClosingDay = anchorEnd.getUTCDate();

  // SMART ANCHOR HANDLING: For dynamic anchor, distinguish between:
  // 1. Actual last cycle end date (from Plaid - keep as-is)
  // 2. Future cycle anchor day (from manual config - use for predictions)
  if (cycleDateType === 'dynamic_anchor' && creditCardWithTransactions.manual_cycle_day) {
    const configuredAnchorDay = Number(creditCardWithTransactions.manual_cycle_day);
    const actualLastCycleEnd = anchorEnd.getUTCDate();

    if (configuredAnchorDay !== actualLastCycleEnd) {
      console.log(`🎯 ANCHOR STRATEGY: Last cycle ended ${actualLastCycleEnd}th (Plaid), future cycles use ${configuredAnchorDay}th anchor (manual config)`);
      console.log(`   → Keep actual end date: ${anchorEnd.toDateString()}`);
      console.log(`   → Use anchor day ${configuredAnchorDay} for future cycle predictions`);

      // Keep anchorEnd as the actual last cycle end (Sep 18)
      // But use configuredAnchorDay for future cycle generation
      baselineClosingDay = configuredAnchorDay;
    }
  }
  
  if (creditCardWithTransactions.manual_dates_configured) {
    if (cycleDateType === 'same_day' && creditCardWithTransactions.manual_cycle_day) {
      baselineClosingDay = Number(creditCardWithTransactions.manual_cycle_day);
    } else if (cycleDateType === 'dynamic_anchor' && creditCardWithTransactions.manual_cycle_day) {
      // For dynamic anchor, use the manual_cycle_day as the anchor target
      baselineClosingDay = Number(creditCardWithTransactions.manual_cycle_day);
    }
  }
  const closingDay = baselineClosingDay;

  // Generate 13 boundaries for 12 historical cycles
  const endBoundaries: Date[] = [];
  let manualForwardEnd: Date | null = null;

  if (cycleDateType === 'dynamic_anchor' && creditCardWithTransactions.manual_dates_configured) {
    console.log(`🎯 Using manual dynamic anchor day ${closingDay} (fixed month boundaries)`);

    // Always retain the actual anchor end for the most recent closed cycle
    endBoundaries.push(toUtcDate(anchorEnd));

    for (let offset = 1; offset <= 12; offset++) {
      const reference = new Date(anchorEnd);
      reference.setUTCMonth(reference.getUTCMonth() - offset);
      const year = reference.getUTCFullYear();
      const month = reference.getUTCMonth();
      const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
      const day = Math.min(closingDay, daysInMonth);
      endBoundaries.push(new Date(Date.UTC(year, month, day)));
    }

    const nextMonthIndex = anchorEnd.getUTCMonth() + 1;
    const nextYear = anchorEnd.getUTCFullYear() + (nextMonthIndex > 11 ? 1 : 0);
    const normalizedNextMonth = nextMonthIndex > 11 ? 0 : nextMonthIndex;
    const daysInNextMonth = new Date(Date.UTC(nextYear, normalizedNextMonth + 1, 0)).getUTCDate();
    manualForwardEnd = new Date(Date.UTC(nextYear, normalizedNextMonth, Math.min(closingDay, daysInNextMonth)));

    console.log('🔧 Manual dynamic anchor boundaries:', endBoundaries.map(date => date.toISOString().split('T')[0]));
  } else if (cycleDateType === 'dynamic_anchor') {
    console.log(`🎯 Using improved dynamic anchor logic with anchor day ${closingDay}`);

    try {
      const { generateImprovedDynamicAnchor } = await import('./improvedDynamicAnchor');

      let institutionName = '';
      try {
        if (creditCardWithTransactions.plaidItemId) {
          const { data: plaidItem } = await supabaseAdmin
            .from('plaid_items')
            .select('institutionName')
            .eq('id', creditCardWithTransactions.plaidItemId)
            .single();
          institutionName = plaidItem?.institutionName || '';
        }
      } catch (_e) {
        console.warn('Could not fetch institution name for improved anchor logic');
      }

      let knownStatementDates: Date[] = [];
      try {
        if (creditCardWithTransactions.plaidItemId) {
          const { data: plaidItem } = await supabaseAdmin
            .from('plaid_items')
            .select('statements_data')
            .eq('id', creditCardWithTransactions.plaidItemId)
            .single();

          if (plaidItem?.statements_data) {
            knownStatementDates = (plaidItem.statements_data as any[])
              .filter(s => s.account_id === creditCardWithTransactions.accountId)
              .map(s => {
                if ((s as any).date_posted) {
                  return new Date((s as any).date_posted);
                }
                const year = parseInt(s.year);
                const month = parseInt(s.month) - 1;
                return new Date(year, month + 1, 0);
              })
              .filter(d => !isNaN(d.getTime()))
              .sort((a, b) => b.getTime() - a.getTime());
          }
        }
      } catch (_e) {
        console.log('No historical statement data available for pattern learning');
      }

      const improvedCycles = generateImprovedDynamicAnchor({
        anchorDay: closingDay,
        startFromDate: anchorEnd,
        monthsToGenerate: 12,
        knownStatementDates: knownStatementDates.length >= 3 ? knownStatementDates : undefined,
        institutionName,
      });

      improvedCycles.forEach(cycle => {
        endBoundaries.push(cycle.endDate);
      });

      const trailingBoundarySource = improvedCycles[improvedCycles.length - 1];
      if (trailingBoundarySource) {
        const trailingBoundary = new Date(trailingBoundarySource.startDate);
        trailingBoundary.setDate(trailingBoundary.getDate() - 1);
        endBoundaries.push(trailingBoundary);
      }

      console.log(`✅ Generated ${endBoundaries.length} improved dynamic anchor boundaries`);
      console.log('🔧 Cycle analysis:', improvedCycles.map(c => ({
        end: c.endDate.toDateString(),
        length: c.cycleLength,
        reason: c.adjustmentReason,
      })));
    } catch (error) {
      console.warn('Failed to use improved dynamic anchor, falling back to original logic:', error);

      const clampDay = (day: number, daysInMonth: number): number => {
        return Math.max(1, Math.min(daysInMonth, day));
      };
      let last31Offset = 0;
      let first31Handled = false;
      let lastCycleLength: number | null = null;
      const dayMs = 24 * 60 * 60 * 1000;

      endBoundaries.push(new Date(anchorEnd));

      for (let m = 1; m <= 12; m++) {
        const currentEnd = endBoundaries[m - 1];
        const currentMonth = currentEnd.getUTCMonth();
        const currentYear = currentEnd.getUTCFullYear();

        let prevMonth = currentMonth - 1;
        let prevYear = currentYear;
        if (prevMonth < 0) {
          prevMonth = 11;
          prevYear--;
        }

        const prevMonthDays = new Date(Date.UTC(prevYear, prevMonth + 1, 0)).getUTCDate();
        let offset: number;

        if (prevMonthDays === 31) {
          if (!first31Handled) {
            offset = 0;
            first31Handled = true;
          } else if (lastCycleLength !== null && lastCycleLength <= 29) {
            offset = 0;
          } else if (last31Offset === -1) {
            offset = 0;
          } else {
            offset = -1;
          }
          last31Offset = offset;
        } else if (prevMonthDays === 30) {
          offset = -1;
          last31Offset = -1;
        } else if (prevMonthDays === 29) {
          offset = -3;
          last31Offset = -1;
        } else {
          offset = -3;
          last31Offset = -1;
        }

        const prevEndDay = clampDay(closingDay + offset, prevMonthDays);
        const prevEnd = createUtcDate(prevYear, prevMonth, prevEndDay);
        endBoundaries.push(prevEnd);

        const cycleStart = new Date(prevEnd);
        cycleStart.setUTCDate(cycleStart.getUTCDate() + 1);
        const cycleLength = Math.round((currentEnd.getTime() - cycleStart.getTime()) / dayMs) + 1;
        lastCycleLength = cycleLength;

        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        console.log(`📊 Fallback dynamic anchor boundary: ${monthNames[prevMonth]} ${prevEndDay} (monthDays=${prevMonthDays}, offset=${offset}, cycleLength=${cycleLength})`);
      }
    }
  } else {
    // Existing logic for same_day and days_before_end
    for (let m = 0; m <= 12; m++) {
      const d = new Date(anchorEnd);
      d.setMonth(d.getMonth() - m);
      const year = d.getFullYear();
      const month = d.getMonth();
      
      // Calculate closing day for this specific month (for days_before_end)
      let monthClosingDay = closingDay;
      if (creditCardWithTransactions.manual_dates_configured) {
        if (cycleDateType === 'days_before_end') {
          const daysBeforeEnd = Number((creditCardWithTransactions as any).cycle_days_before_end);
          if (daysBeforeEnd >= 1 && daysBeforeEnd <= 31) {
            const lastDayOfThisMonth = new Date(year, month + 1, 0).getDate();
            monthClosingDay = Math.max(1, lastDayOfThisMonth - daysBeforeEnd);
          }
        }
      }
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const day = Math.min(monthClosingDay, daysInMonth);
      endBoundaries.push(new Date(Date.UTC(year, month, day)));
    }
  }

  // helper for due date estimation (same day-of-month as current due date)
  const estimateHistoricalDue = (cycleEnd: Date): Date | null => {
    if (!baselineDay) return null;
    
    // For dynamic anchor, calculate due date based on the adjusted closing date + typical grace period
    const dueDateType = (creditCardWithTransactions as any).due_date_type;
    if (dueDateType === 'dynamic_anchor') {
      // Typically 21 days after statement close for dynamic anchor cards
      const dueDate = new Date(cycleEnd);
      dueDate.setDate(dueDate.getDate() + 21);
      
      // If manual due day is configured, try to hit that target day (but adjust if needed)
      if (creditCardWithTransactions.manual_due_day) {
        const targetDueDay = Number(creditCardWithTransactions.manual_due_day);
        const targetMonth = dueDate.getUTCMonth();
        const targetYear = dueDate.getUTCFullYear();
        const daysInDueMonth = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
        const adjustedDueDay = Math.min(targetDueDay, daysInDueMonth);

        // Use the target due day if it's within a reasonable range (±5 days) of calculated due
        const targetDueDate = new Date(Date.UTC(targetYear, targetMonth, adjustedDueDay));
        const daysDiff = Math.abs((targetDueDate.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));

        if (daysDiff <= 5) {
          return targetDueDate;
        }
      }

      return dueDate;
    }

    // Existing logic for same_day and days_before_end
    const y = cycleEnd.getUTCFullYear();
    const m = cycleEnd.getUTCMonth() + 1; // next month
    const y2 = y + (m > 11 ? 1 : 0);
    const m2 = (m % 12);
    const daysInMonth2 = new Date(Date.UTC(y2, m2 + 1, 0)).getUTCDate();
    const day2 = Math.min(baselineDay, daysInMonth2);
    return new Date(Date.UTC(y2, m2, day2));
  };

  // Create 12 historical closed cycles using consecutive boundaries
  for (let i = 0; i < 12; i++) {
    const end = endBoundaries[i];
    const prevEnd = endBoundaries[i + 1];
    const start = new Date(prevEnd);
    start.setDate(start.getDate() + 1);
    const isAnchor = end.getTime() === anchorEnd.getTime();
    const due = isAnchor ? (nextDueDate || null) : estimateHistoricalDue(end);
    await createOrUpdateCycle(
      creditCardWithTransactions,
      cycles,
      start,
      end,
      due,
      isAnchor,
      transactionsWithDates
    );
  }

  // Add current open cycle (from last anchor end + 1 to next anchor day next month)
  const openStart = new Date(anchorEnd);
  openStart.setUTCDate(openStart.getUTCDate() + 1);

  // For dynamic anchor, ensure we always have a current open cycle
  // If today is before or equal to the anchor end date, we need to generate the current cycle
  const today = new Date();
  let openEnd: Date;

  if (cycleDateType === 'dynamic_anchor') {
    if (manualForwardEnd) {
      openEnd = toUtcDate(manualForwardEnd);
      console.log(`🎯 Manual dynamic anchor current cycle: ${openStart.toDateString()} to ${openEnd.toDateString()} (anchor day ${baselineClosingDay})`);
    } else {
      // For dynamic anchor, use forward date calculation for current cycle
      const targetMonth = anchorEnd.getUTCMonth() + 1;
      const targetYear = anchorEnd.getUTCFullYear() + (targetMonth > 11 ? 1 : 0);
      const normalizedMonth = targetMonth > 11 ? 0 : targetMonth;

      const daysInTargetMonth = new Date(targetYear, normalizedMonth + 1, 0).getDate();
      let clampedAnchorDay = Math.min(baselineClosingDay, daysInTargetMonth);

      try {
        if (creditCardWithTransactions.plaidItemId) {
          const { data: plaidItem } = await supabaseAdmin
            .from('plaid_items')
            .select('institutionName')
            .eq('id', creditCardWithTransactions.plaidItemId)
            .single();

          const institutionName = plaidItem?.institutionName || '';
          if (institutionName.toLowerCase().includes('american express')) {
            const tempEnd = createUtcDate(targetYear, normalizedMonth, clampedAnchorDay);
            const tempCycleLength = Math.floor((tempEnd.getTime() - openStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;

            if (tempCycleLength < 28 || tempCycleLength > 32) {
              for (let adjust = -2; adjust <= 2; adjust++) {
                if (adjust === 0) continue;
                const adjustedDay = Math.max(1, Math.min(daysInTargetMonth, clampedAnchorDay + adjust));
                const adjustedEnd = createUtcDate(targetYear, normalizedMonth, adjustedDay);
                const adjustedLength = Math.floor((adjustedEnd.getTime() - openStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;

                if (adjustedLength >= 28 && adjustedLength <= 32) {
                  clampedAnchorDay = adjustedDay;
                  console.log(`🎯 Amex current cycle: Adjusted anchor ${baselineClosingDay} → ${clampedAnchorDay} for better cycle length (${adjustedLength} days)`);
                  break;
                }
              }
            }
          }
        }
      } catch (_e) {
        console.warn('Could not fetch institution for current cycle adjustment');
      }

      openEnd = createUtcDate(targetYear, normalizedMonth, clampedAnchorDay);
      console.log(`🎯 Dynamic anchor current cycle: ${openStart.toDateString()} to ${openEnd.toDateString()} (anchor day ${baselineClosingDay} → ${clampedAnchorDay})`);
    }
  } else {
    // Original logic for same_day and days_before_end
    const y = anchorEnd.getUTCFullYear();
    const m = anchorEnd.getUTCMonth() + 1; // next month
    const y2 = y + (m > 11 ? 1 : 0);
    const m2 = (m % 12);
    const dim = new Date(Date.UTC(y2, m2 + 1, 0)).getUTCDate();

    let targetDay = anchorEnd.getUTCDate();

    // For "days before end" configurations, recalculate the closing day based on the
    // actual number of days in the upcoming month so that long months (31 days)
    // correctly shift the closing date forward. Without this adjustment the open
    // cycle inherits the previous month's end date (e.g., 27th from a 30-day month)
    // which is one day early when the next month has 31 days.
    if (
      cycleDateType === 'days_before_end' &&
      typeof creditCardWithTransactions.cycle_days_before_end === 'number'
    ) {
      const offset = creditCardWithTransactions.cycle_days_before_end;
      const recalculated = dim - offset;
      // Clamp to valid day range (1..dim) to avoid zero or negative values when
      // the offset equals the number of days in shorter months.
      targetDay = Math.max(1, Math.min(dim, recalculated));
    }

    openEnd = createUtcDate(y2, m2, Math.min(targetDay, dim));
  }

  // For current open cycle, don't assign a due date yet
  // The due date will be determined when the cycle closes and a bill is generated
  // Using null prevents incorrectly showing an estimated future due date during regeneration
  await createOrUpdateCycle(
    creditCardWithTransactions,
    cycles,
    openStart,
    openEnd,
    null,
    false,
    transactionsWithDates
  );

  try {
    const isCapOneByName = isCapitalOneCard(undefined, creditCardWithTransactions.name);
    const earliestTxn = (transactionsWithDates && transactionsWithDates.length > 0)
      ? new Date(Math.min(...transactionsWithDates.map(t => t.date.getTime())))
      : null;
    const trueOpen = creditCardWithTransactions.openDate ? new Date(creditCardWithTransactions.openDate) : null;
    const adjustedOpen = isCapOneByName && earliestTxn
      ? (trueOpen ? (earliestTxn > trueOpen ? earliestTxn : trueOpen) : earliestTxn)
      : trueOpen;
    if (adjustedOpen) {
      cycles = cycles.filter(c => new Date(c.endDate) >= adjustedOpen);
    }
  } catch {}
  return cycles.sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
}

async function createOrUpdateCycle(
  creditCard: any,
  cycles: BillingCycleData[],
  cycleStart: Date,
  cycleEnd: Date,
  dueDate: Date | null,
  hasStatementBalance: boolean,
  transactionsWithDates: any[]
): Promise<void> {
  // Adjust cycle end to end-of-day for proper closed/open cycle detection
  // Cycles ending "today" should not be treated as closed until 11:59:59 PM
  const adjustedCycleEnd = adjustCycleEndToEndOfDay(cycleEnd);

  // For current cycles, cap the transaction search at today to get accurate spend calculation
  const today = new Date();
  const effectiveEndDate = adjustedCycleEnd > today ? today : adjustedCycleEnd;
  
  // For Robinhood with manual dates, use authorized date (posted date) for cycle boundaries
  // For all other institutions, continue using transaction date
  const isRobinhoodWithManualDates = creditCard.manual_dates_configured && 
    (creditCard.plaidItem?.institutionId === 'ins_54' || 
     /robinhood/i.test(creditCard.plaidItem?.institutionName || ''));
  
  const cycleTransactions = creditCard.transactions.filter((t: any) => {
    // Exclude pending transactions from all calculations
    if (t.pending === true) {
      return false;
    }
    
    if (isRobinhoodWithManualDates && t.authorizedDate) {
      // For Robinhood with manual dates: use authorized date (posted date)
      return t.authorizedDate >= cycleStart && t.authorizedDate <= effectiveEndDate;
    } else {
      // For all other institutions: use transaction date (existing behavior)
      return t.date >= cycleStart && t.date <= effectiveEndDate;
    }
  });
  
  // Count pending transactions that were excluded
  const pendingCount = creditCard.transactions.filter((t: any) => t.pending === true).length;
  
  if (isRobinhoodWithManualDates) {
    console.log(`🏦 Robinhood manual cycle: Using authorized dates for ${cycleTransactions.length} posted transactions between ${cycleStart.toDateString()} - ${effectiveEndDate.toDateString()} (${pendingCount} pending excluded)`);
  } else if (pendingCount > 0) {
    console.log(`📊 Cycle includes ${cycleTransactions.length} posted transactions (${pendingCount} pending excluded)`);
  }

  // Calculate final transaction count (excluding payments but including refunds/returns)
  const finalTransactionCount = cycleTransactions.filter((t: any) => !isPaymentTransaction(t.name || '')).length;

  console.log(`📊 Transaction count calculation for ${creditCard.name} cycle ${cycleStart.toDateString()} - ${cycleEnd.toDateString()}:`, {
    totalInPeriod: cycleTransactions.length,
    excludingPayments: finalTransactionCount,
    paymentsExcluded: cycleTransactions.length - finalTransactionCount
  });

  // Properly calculate spend: include charges and refunds, but exclude payments
  let totalSpend = cycleTransactions.reduce((sum: number, t: any) => {
    // Check if this is a payment transaction based on the name
    if (isPaymentTransaction(t.name)) {
      // Skip payment transactions regardless of sign
      return sum;
    }

    // Include all non-payment transactions:
    // - Positive amounts = charges/purchases
    // - Negative amounts = refunds/returns (should be included to reduce spend)
    return sum + t.amount;
  }, 0);
  
  // For current cycles, use transaction-based calculation from actual transactions in the cycle
  if (adjustedCycleEnd > today && !hasStatementBalance) {
    // Filter to only non-pending transactions (same criteria as transaction count)
    const postedTransactions = cycleTransactions.filter((t: any) =>
      !t.pending
    );

    const postedSpend = postedTransactions.reduce((sum: number, t: any) => {
      // Exclude payment transactions, include charges and refunds
      if (isPaymentTransaction(t.name)) {
        return sum; // Skip payments
      }
      return sum + t.amount; // Include charges (positive) and refunds (negative)
    }, 0);

    // Use transaction-based calculation for current cycles (from actual cycle transactions)
    totalSpend = postedSpend;

    console.log(`📊 Current cycle spend for ${creditCard.name}: ${totalSpend} (${postedTransactions.length} posted transactions in cycle)`);
  }
  
  // For closed cycles, only use the actual statement balance for the EXACT statement cycle
  // For all other historical cycles, use transaction-based totals
  const lastStatementDate = creditCard.lastStatementIssueDate ? new Date(creditCard.lastStatementIssueDate) : null;
  const isExactStatementCycle = lastStatementDate && cycleEnd.getTime() === lastStatementDate.getTime();
  
  if (hasStatementBalance && creditCard.lastStatementBalance && isExactStatementCycle) {
    // Even for statement cycles, we should use transaction-based totals for totalSpend
    // The statement balance includes payments, but totalSpend should be actual spending only
    const statementAmount = Math.abs(creditCard.lastStatementBalance);
    console.log(`📊 Statement cycle for ${creditCard.name} ending ${safeToISOString(cycleEnd, 'cycleEnd log')?.split('T')[0]}`);
    console.log(`   Statement balance: $${statementAmount.toFixed(2)}`);
    console.log(`   Actual spend (excluding payments): $${totalSpend.toFixed(2)}`);
    // Keep the transaction-based totalSpend, don't override with statement balance
    // totalSpend already calculated above excludes payments correctly
  }

  // Check if cycle already exists - match by start date to prevent overlapping cycles
  const { data: existingCycle, error: findError } = await supabaseAdmin
    .from('billing_cycles')
    .select('*')
    .eq('creditCardId', creditCard.id)
    .eq('startDate', safeToISOString(cycleStart, 'cycleStart query'))
    .single();

  if (findError && findError.code !== 'PGRST116') { // PGRST116 is "no rows returned"
    throw new Error(`Failed to check existing cycle: ${findError.message}`);
  }

  let statementBalance = null;
  let minimumPayment = null;
  
  console.log(`📊 Billing cycle processing for ${creditCard.name} ending ${cycleEnd.toDateString()}:`, {
    hasStatementBalance,
    cycleEnd: cycleEnd.toDateString(),
    totalSpend
  });
  
  if (hasStatementBalance) {
    const lastStatementDate = creditCard.lastStatementIssueDate ? new Date(creditCard.lastStatementIssueDate) : null;

    // Check if this is the statement cycle (most recent closed cycle still within payment period)
    const today = new Date();
    const isClosedCycle = adjustedCycleEnd < today;
    const isWithinPaymentPeriod = dueDate && dueDate >= today;
    const isStatementCycle = (lastStatementDate && cycleEnd.getTime() === lastStatementDate.getTime()) ||
                           (isClosedCycle && isWithinPaymentPeriod && hasStatementBalance);

    console.log(`🏦 Statement cycle identification for ${creditCard.name} cycle ending ${cycleEnd.toDateString()}:`, {
      lastStatementDate: lastStatementDate?.toDateString(),
      isClosedCycle,
      isWithinPaymentPeriod,
      hasStatementBalance,
      isStatementCycle,
      dueDate: dueDate?.toDateString(),
      exactDateMatch: lastStatementDate && cycleEnd.getTime() === lastStatementDate.getTime(),
      willGetPaymentDetection: isStatementCycle,
      adjustedCycleEnd: adjustedCycleEnd.toISOString()
    });
    
    if (isStatementCycle) {
      console.log(`🎯 STATEMENT CYCLE CONFIRMED for ${creditCard.name} ending ${cycleEnd.toDateString()} - will check for payments`);
    } else {
      console.log(`❌ NOT statement cycle for ${creditCard.name} ending ${cycleEnd.toDateString()} - no payment detection`);
    }
    
    if (isStatementCycle) {
      // This is the exact cycle that corresponds to the last statement
      statementBalance = creditCard.lastStatementBalance;
      minimumPayment = creditCard.minimumPaymentAmount;
      
      // Payment detection: If current balance is lower than statement balance,
      // find recent payment transactions and subtract them from statement balance
      const currentBalance = Math.abs(creditCard.balanceCurrent || 0);
      const originalStatementBalance = Math.abs(creditCard.lastStatementBalance || 0);
      
      console.log(`🔍 Payment detection debug for ${creditCard.name}:`, {
        currentBalance,
        originalStatementBalance,
        conditionMet: originalStatementBalance > 0,
        statementDate: lastStatementDate?.toDateString(),
        transactioncount: finalTransactionCount
      });

      if (originalStatementBalance > 0) {
        // Look for payment transactions since the last statement date
        const statementDate = lastStatementDate;
        
        // Debug: Show ALL recent transactions first
        const allRecentTransactions = transactionsWithDates.filter(t => 
          statementDate && t.date > statementDate
        );
        
        console.log(`📅 All transactions after ${statementDate?.toDateString()}:`, 
          allRecentTransactions.map(t => ({
            name: t.name,
            amount: t.amount,
            date: t.date.toDateString(),
            isPayment: isPaymentTransaction(t.name),
            isNegative: t.amount < 0
          }))
        );
        
        const recentPayments = transactionsWithDates.filter(t => 
          statementDate && t.date > statementDate && // After statement date
          isPaymentTransaction(t.name) // Is a payment transaction (amount sign may vary)
        );
        
        console.log(`💰 Filtered payment transactions:`, recentPayments.length);
        
        // Sum up the payment amounts (they're negative, so we need to make them positive)
        const totalPayments = recentPayments.reduce((sum, t) => sum + Math.abs(t.amount), 0);
        
        // Check if there's a payment that matches the statement balance (within $0.50 tolerance)
        const statementMatchingPayment = recentPayments.find(t => {
          const paymentAmount = Math.abs(t.amount);
          const difference = Math.abs(paymentAmount - originalStatementBalance);
          return difference <= 0.5; // Allow $0.50 tolerance for rounding/fees
        });
        
        if (statementMatchingPayment) {
          console.log(`✅ Found statement balance payment for ${creditCard.name}:`, {
            paymentAmount: Math.abs(statementMatchingPayment.amount),
            statementBalance: originalStatementBalance,
            difference: Math.abs(Math.abs(statementMatchingPayment.amount) - originalStatementBalance),
            paymentDate: statementMatchingPayment.date.toDateString(),
            paymentName: statementMatchingPayment.name
          });
        }
        
        if (totalPayments > 0) {
          console.log(`💳 Payment detected for ${creditCard.name}:`, {
            currentBalance,
            originalStatementBalance,
            totalPayments,
            recentPayments: recentPayments.map(p => ({ name: p.name, amount: p.amount, date: p.date }))
          });
          
          // Keep the original cycle balance (totalSpend) for display
          // The payment detection is for card-level statement balance, not individual cycles
          statementBalance = totalSpend > 0 ? totalSpend : Math.abs(creditCard.lastStatementBalance || 0);
          
          // Adjust minimum payment if statement was paid off
          const remainingStatementBalance = Math.max(0, originalStatementBalance - totalPayments);
          if (remainingStatementBalance === 0) {
            minimumPayment = 0;

            // Update nextPaymentDueDate to the new cycle's due date
            // Calculate due date for the NEW current cycle (typically 25 days after this closed cycle)
            const newDueDate = new Date(cycleEnd);
            newDueDate.setDate(newDueDate.getDate() + 25);

            // Update the card's nextPaymentDueDate
            await supabaseAdmin
              .from('credit_cards')
              .update({ nextPaymentDueDate: safeToISOString(newDueDate, 'newDueDate payment update') })
              .eq('id', creditCard.id);

            console.log(`✅ PAYMENT DETECTED - Setting minimumPayment = 0 for ${creditCard.name} cycle ending ${cycleEnd.toDateString()}`, {
              originalStatementBalance,
              totalPayments,
              remainingStatementBalance,
              currentBalance,
              cycleEndDate: cycleEnd.toDateString(),
              paymentTransactions: recentPayments.map(p => ({
                name: p.name,
                amount: p.amount,
                date: p.date.toDateString()
              }))
            });

            console.log(`✅ Updated nextPaymentDueDate for paid-off cycle`, {
              cardName: creditCard.name,
              oldDueDate: creditCard.nextPaymentDueDate,
              newDueDate: newDueDate.toDateString()
            });
          } else {
            console.log(`⚠️ NO PAYMENT DETECTED for ${creditCard.name} cycle ending ${cycleEnd.toDateString()}`, {
              originalStatementBalance,
              totalPayments,
              remainingStatementBalance,
              currentBalance,
              recentPaymentsCount: recentPayments.length
            });
          }
        }
      }
    } else {
      // This is a historical completed cycle - ALWAYS use calculated spend from transactions
      // Don't use the last statement balance for historical cycles
      statementBalance = totalSpend > 0 ? totalSpend : 0; // Use 0 instead of null for empty cycles
      minimumPayment = totalSpend > 0 ? Math.max(25, totalSpend * 0.02) : 0; // Estimate 2% minimum payment
    }
  }

  if (!existingCycle) {
    // Create new cycle
    const { data: newCycle, error: createError } = await supabaseAdmin
      .from('billing_cycles')
      .insert({
        id: crypto.randomUUID(),
        creditCardId: creditCard.id,
        creditcardname: creditCard.name,
        transactioncount: finalTransactionCount,
        startDate: safeToISOString(cycleStart, 'cycleStart insertion'),
        endDate: safeToISOString(adjustedCycleEnd, 'cycleEnd insertion'),
        statementBalance,
        minimumPayment,
        dueDate: safeToISOString(dueDate, 'dueDate insertion'),
        totalSpend,
        updatedAt: safeToISOString(new Date(), 'updatedAt field'),
      })
      .select();

    if (createError || !newCycle || newCycle.length === 0) {
      throw new Error(`Failed to create billing cycle: ${createError?.message || 'No data returned'}`);
    }
    
    const createdCycle = Array.isArray(newCycle) ? newCycle[0] : newCycle;

    cycles.push({
      id: createdCycle.id,
      creditCardId: creditCard.id,
      creditCardName: creditCard.name,
      creditCardMask: creditCard.mask,
      startDate: cycleStart,
      endDate: cycleEnd, // Use the original date (midnight) for display, not adjusted
      statementBalance: statementBalance !== null ? statementBalance : undefined,
      minimumPayment: minimumPayment !== null ? minimumPayment : undefined,
      dueDate: dueDate || undefined,
      totalSpend,
      transactioncount: finalTransactionCount,
    });
  } else {
    console.log(`🔄 Updating existing billing cycle for ${creditCard.name} starting ${cycleStart.toDateString()}`);

    const newEndDateISO = safeToISOString(adjustedCycleEnd, 'cycleEnd comparison');
    if (existingCycle.endDate !== newEndDateISO) {
      console.log(`📅 End date changed: ${existingCycle.endDate} → ${newEndDateISO}`);
    }

    // Always update existing cycles to ensure transaction-based totals are current
    const { data: updatedCycle, error: updateError } = await supabaseAdmin
      .from('billing_cycles')
      .update({
        creditcardname: creditCard.name,
        transactioncount: finalTransactionCount,
        endDate: safeToISOString(adjustedCycleEnd, 'cycleEnd update'), // Update end date if calculation changed
        statementBalance,
        minimumPayment,
        dueDate: safeToISOString(dueDate, 'dueDate update'),
        totalSpend,
        updatedAt: safeToISOString(new Date(), 'updatedAt field'),
      })
      .eq('id', existingCycle.id)
      .select();

    if (updateError || !updatedCycle || updatedCycle.length === 0) {
      console.error(`Failed to update billing cycle ${existingCycle.id}:`, updateError);
      throw new Error(`Failed to update billing cycle: ${updateError?.message || 'No rows affected'}`);
    }
    
    const modifiedCycle = Array.isArray(updatedCycle) ? updatedCycle[0] : updatedCycle;

    cycles.push({
      id: modifiedCycle.id,
      creditCardId: creditCard.id,
      creditCardName: creditCard.name,
      creditCardMask: creditCard.mask,
      startDate: cycleStart,
      endDate: cycleEnd, // Use the original date (midnight) for display, not adjusted
      statementBalance: statementBalance !== null ? statementBalance : undefined,
      minimumPayment: minimumPayment !== null ? minimumPayment : undefined,
      dueDate: dueDate || undefined,
      totalSpend,
      transactioncount: finalTransactionCount,
    });
  }
}

// Helper function to estimate cycle length based on statement and due dates
function estimateCycleLength(creditCard: any, lastStatementDate: Date, nextDueDate: Date | null): number {
  // Default cycle length is 30 days
  let cycleLength = 30;

  if (nextDueDate) {
    // Estimate cycle length based on grace period (typically 21-25 days)
    const gracePeriod = differenceInDays(nextDueDate, lastStatementDate);

    if (gracePeriod >= 20 && gracePeriod <= 25) {
      cycleLength = 30; // Standard monthly cycle
    } else if (gracePeriod >= 26 && gracePeriod <= 32) {
      cycleLength = 31; // Slightly longer cycle
    }
  }

  return cycleLength;
}

export async function getAllUserBillingCycles(userId: string): Promise<BillingCycleData[]> {
  // Get all plaid items for the user
  const { data: plaidItems, error: plaidError } = await supabaseAdmin
    .from('plaid_items')
    .select('*')
    .eq('userId', userId);

  if (plaidError) {
    throw new Error(`Failed to fetch plaid items: ${plaidError.message}`);
  }

  // Get all credit cards for these plaid items
  const plaidItemIds = (plaidItems || []).map(item => item.id);
  if (plaidItemIds.length === 0) {
    return [];
  }

  const { data: creditCards, error: cardsError } = await supabaseAdmin
    .from('credit_cards')
    .select('*')
    .in('plaidItemId', plaidItemIds);

  if (cardsError) {
    throw new Error(`Failed to fetch credit cards: ${cardsError.message}`);
  }

  const allCycles: BillingCycleData[] = [];
  
  // Create a map of plaidItem data for easy lookup
  const plaidItemMap = new Map();
  (plaidItems || []).forEach(item => {
    plaidItemMap.set(item.id, item);
  });
  
  for (const card of creditCards || []) {
    // Get the associated plaid item (used multiple times in this loop)
    const plaidItem = plaidItemMap.get(card.plaidItemId);
    
    // Attempt statements-based periods when possible (using cached support status)
    let statementPeriods: { startDate: Date | null; endDate: Date }[] | null = null;
    try {
      const isRobinhood = plaidItem?.institutionId === 'ins_54' || /robinhood/i.test(plaidItem?.institutionName || '');
      
      // Check cached statements support to avoid API calls
      const hasStatementsSupport = plaidItem?.statements_enabled === true;
      const statementsLastChecked = plaidItem?.statements_last_checked ? new Date(plaidItem.statements_last_checked) : null;
      const isStale = !statementsLastChecked || (Date.now() - statementsLastChecked.getTime()) > (24 * 60 * 60 * 1000);
      
      if (!isRobinhood && hasStatementsSupport && plaidItem?.accessToken && card.accountId) {
        console.log(`📄 Using cached statements support for ${card.name} (enabled: ${hasStatementsSupport})`);

        // Dynamic import to avoid bundling server-only modules into client
        const { decrypt } = await import('@/lib/encryption');
        const { listStatementPeriods } = await import('@/services/plaidStatements');
        const accessToken = decrypt(plaidItem.accessToken);

        try {
          const periods: any[] = await listStatementPeriods(accessToken, card.accountId, 13, plaidItem?.institutionName, plaidItem?.id);
          console.log(`📄 Retrieved ${periods.length} statement periods for ${card.name}`);

          // Use only periods that have both start and end (skip newest if start is null)
          const usable = periods
            .filter(p => p.endDate && (p.startDate instanceof Date))
            .map(p => ({ startDate: p.startDate!, endDate: p.endDate }));

          if (usable.length > 0) {
            statementPeriods = usable;
            console.log(`✅ Using ${usable.length} usable statement periods for ${card.name}:`,
              usable.slice(0, 3).map(p => ({
                start: p.startDate.toDateString(),
                end: p.endDate.toDateString()
              }))
            );

            // For Amex cards specifically, analyze the pattern
            if (plaidItem?.institutionName?.toLowerCase().includes('american express')) {
              try {
                const { analyzeStatementPatterns } = await import('./improvedDynamicAnchor');
                const endDates = usable.map(p => p.endDate);
                const analysis = analyzeStatementPatterns(endDates);
                console.log(`🎯 Amex statement pattern analysis:`, {
                  complexity: analysis.patternComplexity,
                  avgCycleLength: analysis.avgCycleLength.toFixed(1),
                  recommendedAnchor: analysis.recommendedAnchorDay,
                  insights: analysis.insights
                });
              } catch (e) {
                console.warn('Failed to analyze Amex statement pattern:', e);
              }
            }
          } else {
            console.warn(`⚠️ No usable statement periods found for ${card.name} (${periods.length} total periods, but missing start dates)`);
          }
        } catch (error) {
          console.error(`❌ Failed to fetch statements for ${card.name}:`, error);
        }
      } else if (!isRobinhood && isStale && plaidItem?.accessToken) {
        // If statements support hasn't been checked recently, refresh it in background
        console.log(`🔄 Refreshing statements support cache for ${card.name} (stale by ${Math.round((Date.now() - (statementsLastChecked?.getTime() || 0)) / (60 * 60 * 1000))} hours)`);
        (async () => {
          try {
            const { decrypt } = await import('@/lib/encryption');
            const { checkAndCacheStatementsSupport } = await import('@/services/plaidStatements');
            const accessToken = decrypt(plaidItem.accessToken);
            await checkAndCacheStatementsSupport(accessToken, plaidItem.id, true);
          } catch (e) {
            console.warn('Background statements support refresh failed:', e);
          }
        })();
      } else if (!hasStatementsSupport) {
        console.log(`📄 Skipping statements for ${card.name}: ${isRobinhood ? 'Robinhood' : 'statements not enabled'}`);
      }
    } catch (e) {
      console.warn('Statements-based period listing failed; falling back to heuristic cycles:', e);
    }

    // For Robinhood without manual configuration, do not generate/display cycles yet
    const isRobinhood = plaidItem?.institutionId === 'ins_54' || /robinhood/i.test(plaidItem?.institutionName || '');
    const manualConfigured = !!(card as any).manual_dates_configured;
    if (isRobinhood && !manualConfigured) {
      // Skip cycle generation; transactions will still sync in background
      continue;
    }

    const cycles = await calculateBillingCycles(card.id, {
      statementPeriods: statementPeriods || undefined,
      baselineDueDate: card.nextPaymentDueDate ? new Date(card.nextPaymentDueDate) : null,
    });
    
    // Filter cycles to only include those that end after card open date (overlaps with card opening)
    let filteredCycles = cycles;
    if (card.openDate) {
      const cardOpenDate = new Date(card.openDate);
      
      filteredCycles = cycles.filter(cycle => {
        const cycleEnd = new Date(cycle.endDate);
        // A cycle is valid if it ends after the card open date (overlaps with card opening)
        // This allows partial cycles where the start is before open date but end is after
        const isValid = cycleEnd >= cardOpenDate;
        
        return isValid;
      });
    }
    
    // Apply Capital One-specific cycle limiting
    const isCapitalOne = isCapitalOneCard(plaidItem?.institutionName, card.name);
    
    // Debug: Log cycle limiting decisions
    console.log(`🔍 Cycle limiting decision for ${card.name}:`, {
      cardName: card.name,
      institutionName: plaidItem?.institutionName,
      isCapitalOne,
      totalCyclesBeforeLimit: filteredCycles.length
    });
    
    if (isCapitalOne) {
      // For Capital One cards, limit to 4 most recent cycles (90 days = ~3-4 billing cycles)
      const limitedCycles = filteredCycles
        .sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime())
        .slice(0, 4);
      
      allCycles.push(...limitedCycles);
    } else {
      // Standard cards: show 12 months of cycles
      const twelveMonthsAgo = new Date();
      twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
      
      // Filter to only cycles from the last 12 months
      const recentCycles = filteredCycles.filter(cycle => 
        new Date(cycle.endDate) >= twelveMonthsAgo
      );
      
      allCycles.push(...recentCycles);
    }
  }

  return allCycles.sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
}

/**
 * Calculate only the current/most recent billing cycle for fast loading
 * Used for instant card setup to show immediate card data
 */
export async function calculateCurrentBillingCycle(creditCardId: string): Promise<BillingCycleData | null> {
  try {
    // Get credit card data
    const { data: creditCard, error: cardError } = await supabaseAdmin
      .from('credit_cards')
      .select('*')
      .eq('id', creditCardId)
      .single();

    if (cardError || !creditCard) {
      console.warn('Credit card not found for current cycle calculation');
      return null;
    }

    // Check if this is a Robinhood card without proper data sources
    // Only skip cycle generation if we have NEITHER liabilities data NOR manual configuration
    let isRobinhoodCard = false;
    
    if (creditCard.plaidItemId) {
      try {
        const { data: plaidItem, error: plaidItemError } = await supabaseAdmin
          .from('plaid_items')
          .select('institutionId, institutionName')
          .eq('id', creditCard.plaidItemId)
          .single();
        
        if (plaidItemError) {
          console.warn(`⚠️  Orphaned Plaid item reference detected for card ${creditCard.name}: ${creditCard.plaidItemId}`, plaidItemError);
          // Continue processing but treat as non-Robinhood card
        } else {
          isRobinhoodCard = plaidItem?.institutionId === 'ins_54';
        }
      } catch (error) {
        console.error('Error checking Plaid item for Robinhood detection:', error);
        // Continue processing but treat as non-Robinhood card
      }
    }
    
    if (isRobinhoodCard && !creditCard.manual_dates_configured && !creditCard.lastStatementIssueDate) {
      console.log('🔒 Skipping cycle calculation for Robinhood card - no reliable data source (needs manual config or liabilities data)');
      return null;
    }
    
    if (isRobinhoodCard) {
      console.log('✅ Robinhood card has reliable data source:', {
        hasManualConfig: !!creditCard.manual_dates_configured,
        hasLiabilitiesData: !!creditCard.lastStatementIssueDate
      });
    }

    // Get only recent transactions (last 60 days) for current cycle
    const sixtyDaysAgo = new Date();
    sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);

    const { data: recentTransactions, error: transactionsError } = await supabaseAdmin
      .from('transactions')
      .select('*')
      .eq('creditCardId', creditCardId)
      .gte('date', safeToISOString(sixtyDaysAgo, 'sixtyDaysAgo transaction query'))
      .order('date', { ascending: true });

    if (transactionsError) {
      console.warn('Failed to fetch recent transactions for current cycle:', transactionsError);
      return null;
    }

    // Convert date strings to Date objects
    const transactionsWithDates = (recentTransactions || []).map(t => ({
      ...t,
      date: new Date(t.date),
      authorizedDate: t.authorizedDate ? new Date(t.authorizedDate) : null,
    }));

    // Calculate current/most recent cycle
    let currentCycleStart: Date;
    let currentCycleEnd: Date;

    if (creditCard.lastStatementIssueDate) {
      // Use statement date as base for current cycle
      const lastStatementDate = new Date(creditCard.lastStatementIssueDate);
      currentCycleStart = lastStatementDate;
      currentCycleEnd = new Date(lastStatementDate);
      currentCycleEnd.setMonth(currentCycleEnd.getMonth() + 1);
    } else {
      // Estimate current cycle from transaction patterns
      const now = new Date();
      currentCycleEnd = now;
      currentCycleStart = new Date(now);
      currentCycleStart.setMonth(currentCycleStart.getMonth() - 1);
    }

    // Get transactions for this cycle
    // For Robinhood with manual dates, use authorized date (posted date) for cycle boundaries
    // For all other institutions, continue using transaction date
    const isRobinhoodWithManualDates = creditCard.manual_dates_configured && 
      (creditCard.plaidItemId && 
       (await supabaseAdmin.from('plaid_items').select('institutionId, institutionName').eq('id', creditCard.plaidItemId).single())
        .data?.institutionId === 'ins_54' || 
       /robinhood/i.test((await supabaseAdmin.from('plaid_items').select('institutionId, institutionName').eq('id', creditCard.plaidItemId).single())
        .data?.institutionName || ''));
    
    const cycleTransactions = transactionsWithDates.filter(t => {
      // Exclude pending transactions from all calculations
      if (t.pending === true) {
        return false;
      }
      
      if (isRobinhoodWithManualDates && t.authorizedDate) {
        // For Robinhood with manual dates: use authorized date (posted date)
        return t.authorizedDate >= currentCycleStart && t.authorizedDate <= currentCycleEnd;
      } else {
        // For all other institutions: use transaction date (existing behavior)
        return t.date >= currentCycleStart && t.date <= currentCycleEnd;
      }
    });
    
    if (isRobinhoodWithManualDates) {
      console.log(`🏦 Robinhood current cycle: Using authorized dates for ${cycleTransactions.length} transactions between ${currentCycleStart.toDateString()} - ${currentCycleEnd.toDateString()}`);
    }

    // Calculate spending for this cycle (exclude payments and pending transactions)
    const nonPaymentTransactions = cycleTransactions.filter(t => !isPaymentTransaction(t.name || '') && !t.pending);
    const totalSpend = nonPaymentTransactions.reduce((sum, t) => sum + t.amount, 0);

    // Create or update the current billing cycle
    const cycleId = `${creditCardId}_${safeToISOString(currentCycleStart, 'currentCycleStart ID')?.split('T')[0]}_${safeToISOString(currentCycleEnd, 'currentCycleEnd ID')?.split('T')[0]}`;

    const { data: existingCycle } = await supabaseAdmin
      .from('billing_cycles')
      .select('*')
      .eq('creditCardId', creditCardId)
      .eq('startDate', safeToISOString(currentCycleStart, 'currentCycleStart query')?.split('T')[0])
      .single();

    if (existingCycle) {
      // Update existing cycle
      const { data: updatedCycle, error: updateError } = await supabaseAdmin
        .from('billing_cycles')
        .update({
          totalSpend,
          transactioncount: nonPaymentTransactions.length,
          updatedAt: safeToISOString(new Date(), 'updatedAt field'),
        })
        .eq('id', existingCycle.id)
        .select()
        .single();

      if (updateError) {
        console.error('Failed to update current billing cycle:', updateError);
        return null;
      }

      return {
        id: updatedCycle.id,
        creditCardId: creditCard.id,
        creditCardName: creditCard.name,
        creditCardMask: creditCard.mask,
        startDate: currentCycleStart,
        endDate: currentCycleEnd,
        statementBalance: updatedCycle.statementBalance || undefined,
        minimumPayment: updatedCycle.minimumPayment || undefined,
        dueDate: updatedCycle.dueDate ? new Date(updatedCycle.dueDate) : undefined,
        totalSpend,
        transactioncount: nonPaymentTransactions.length,
      };
    } else {
      // Create new current cycle
      // Adjust cycle end date to 11:59:59 PM so cycles ending "today" aren't treated as closed
      const adjustedCycleEnd = adjustCycleEndToEndOfDay(currentCycleEnd);

      const { data: newCycle, error: insertError } = await supabaseAdmin
        .from('billing_cycles')
        .insert({
          id: cycleId,
          creditCardId: creditCard.id,
          startDate: safeToISOString(currentCycleStart, 'currentCycleStart insertion')?.split('T')[0],
          endDate: safeToISOString(adjustedCycleEnd, 'currentCycleEnd insertion'),
          totalSpend,
          transactioncount: nonPaymentTransactions.length,
          paymentstatus: 'current', // Current cycle is always in progress
          createdAt: safeToISOString(new Date(), 'createdAt field'),
          updatedAt: safeToISOString(new Date(), 'updatedAt field'),
        })
        .select()
        .single();

      if (insertError) {
        console.error('Failed to create current billing cycle:', insertError);
        return null;
      }

      return {
        id: newCycle.id,
        creditCardId: creditCard.id,
        creditCardName: creditCard.name,
        creditCardMask: creditCard.mask,
        startDate: currentCycleStart,
        endDate: currentCycleEnd,
        totalSpend,
        transactioncount: nonPaymentTransactions.length,
      };
    }
  } catch (error) {
    console.error('Error calculating current billing cycle:', error);
    return null;
  }
}

/**
 * Calculate the most recent closed billing cycle for fast loading
 * Used for instant card setup to show the recently closed cycle with totalSpend
 */
export async function calculateRecentClosedCycle(creditCardId: string): Promise<BillingCycleData | null> {
  try {
    // Get credit card data
    const { data: creditCard, error: cardError } = await supabaseAdmin
      .from('credit_cards')
      .select('*')
      .eq('id', creditCardId)
      .single();

    if (cardError || !creditCard) {
      console.warn('Credit card not found for recent closed cycle calculation');
      return null;
    }

    // Check if this is a Robinhood card without proper data sources
    // Only skip cycle generation if we have NEITHER liabilities data NOR manual configuration
    let isRobinhoodCard = false;
    
    if (creditCard.plaidItemId) {
      try {
        const { data: plaidItem, error: plaidItemError } = await supabaseAdmin
          .from('plaid_items')
          .select('institutionId, institutionName')
          .eq('id', creditCard.plaidItemId)
          .single();
        
        if (plaidItemError) {
          console.warn(`⚠️  Orphaned Plaid item reference detected for card ${creditCard.name}: ${creditCard.plaidItemId}`, plaidItemError);
          // Continue processing but treat as non-Robinhood card
        } else {
          isRobinhoodCard = plaidItem?.institutionId === 'ins_54';
        }
      } catch (error) {
        console.error('Error checking Plaid item for Robinhood detection:', error);
        // Continue processing but treat as non-Robinhood card
      }
    }
    
    if (isRobinhoodCard && !creditCard.manual_dates_configured && !creditCard.lastStatementIssueDate) {
      console.log('🔒 Skipping recent closed cycle calculation for Robinhood card - no reliable data source (needs manual config or liabilities data)');
      return null;
    }
    
    if (isRobinhoodCard) {
      console.log('✅ Robinhood recent closed cycle has reliable data source:', {
        hasManualConfig: !!creditCard.manual_dates_configured,
        hasLiabilitiesData: !!creditCard.lastStatementIssueDate
      });
    }

    // Get recent transactions (last 90 days) to capture the closed cycle
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

    const { data: recentTransactions, error: transactionsError } = await supabaseAdmin
      .from('transactions')
      .select('*')
      .eq('creditCardId', creditCardId)
      .gte('date', safeToISOString(ninetyDaysAgo, 'ninetyDaysAgo transaction query'))
      .order('date', { ascending: true });

    if (transactionsError) {
      console.warn('Failed to fetch recent transactions for closed cycle:', transactionsError);
      return null;
    }

    // Convert date strings to Date objects
    const transactionsWithDates = (recentTransactions || []).map(t => ({
      ...t,
      date: new Date(t.date),
      authorizedDate: t.authorizedDate ? new Date(t.authorizedDate) : null,
    }));

    // Calculate recent closed cycle (previous month)
    let closedCycleStart: Date;
    let closedCycleEnd: Date;

    if (creditCard.lastStatementIssueDate) {
      // Use statement date as base - go back one cycle
      const lastStatementDate = new Date(creditCard.lastStatementIssueDate);
      closedCycleEnd = lastStatementDate;
      closedCycleStart = new Date(lastStatementDate);
      closedCycleStart.setMonth(closedCycleStart.getMonth() - 1);
    } else {
      // Estimate closed cycle from current date - go back one month
      const now = new Date();
      closedCycleStart = new Date(now);
      closedCycleStart.setMonth(closedCycleStart.getMonth() - 2); // 2 months back for start
      closedCycleEnd = new Date(now);
      closedCycleEnd.setMonth(closedCycleEnd.getMonth() - 1); // 1 month back for end
    }

    // Get transactions for this closed cycle
    // For Robinhood with manual dates, use authorized date (posted date) for cycle boundaries
    // For all other institutions, continue using transaction date
    const isRobinhoodWithManualDates = creditCard.manual_dates_configured && 
      (creditCard.plaidItemId && 
       (await supabaseAdmin.from('plaid_items').select('institutionId, institutionName').eq('id', creditCard.plaidItemId).single())
        .data?.institutionId === 'ins_54' || 
       /robinhood/i.test((await supabaseAdmin.from('plaid_items').select('institutionId, institutionName').eq('id', creditCard.plaidItemId).single())
        .data?.institutionName || ''));
    
    const cycleTransactions = transactionsWithDates.filter(t => {
      // Exclude pending transactions from all calculations
      if (t.pending === true) {
        return false;
      }
      
      if (isRobinhoodWithManualDates && t.authorizedDate) {
        // For Robinhood with manual dates: use authorized date (posted date)
        return t.authorizedDate >= closedCycleStart && t.authorizedDate <= closedCycleEnd;
      } else {
        // For all other institutions: use transaction date (existing behavior)
        return t.date >= closedCycleStart && t.date <= closedCycleEnd;
      }
    });
    
    if (isRobinhoodWithManualDates) {
      console.log(`🏦 Robinhood closed cycle: Using authorized dates for ${cycleTransactions.length} transactions between ${closedCycleStart.toDateString()} - ${closedCycleEnd.toDateString()}`);
    }

    // Skip if no transactions found for this period
    if (cycleTransactions.length === 0) {
      console.log('No transactions found for recent closed cycle period');
      return null;
    }

    // Calculate spending for this cycle (exclude payments and pending transactions)
    const nonPaymentTransactions = cycleTransactions.filter(t => !isPaymentTransaction(t.name || '') && !t.pending);
    const totalSpend = nonPaymentTransactions.reduce((sum, t) => sum + t.amount, 0);

    // Check if this cycle matches Plaid's statement date
    const lastStatementDate = creditCard.lastStatementIssueDate ? new Date(creditCard.lastStatementIssueDate) : null;
    const isExactStatementCycle = lastStatementDate && closedCycleEnd.getTime() === lastStatementDate.getTime();
    
    // Only use Plaid's statement balance for the exact matching cycle
    const cycleStatementBalance = isExactStatementCycle && creditCard.lastStatementBalance 
      ? creditCard.lastStatementBalance 
      : totalSpend;

    // Create or update the closed billing cycle
    const cycleId = `${creditCardId}_${safeToISOString(closedCycleStart, 'closedCycleStart ID')?.split('T')[0]}_${safeToISOString(closedCycleEnd, 'closedCycleEnd ID')?.split('T')[0]}`;

    const { data: existingCycle } = await supabaseAdmin
      .from('billing_cycles')
      .select('*')
      .eq('creditCardId', creditCardId)
      .eq('startDate', safeToISOString(closedCycleStart, 'closedCycleStart query')?.split('T')[0])
      .single();

    if (existingCycle) {
      // Update existing closed cycle
      const { data: updatedCycle, error: updateError } = await supabaseAdmin
        .from('billing_cycles')
        .update({
          totalSpend,
          transactioncount: nonPaymentTransactions.length,
          // Use the determined statement balance (either Plaid's for matching cycle or totalSpend)
          statementBalance: cycleStatementBalance,
          paymentstatus: 'due', // Closed cycles are typically due
          updatedAt: safeToISOString(new Date(), 'updatedAt field'),
        })
        .eq('id', existingCycle.id)
        .select()
        .single();

      if (updateError) {
        console.error('Failed to update recent closed billing cycle:', updateError);
        return null;
      }

      return {
        id: updatedCycle.id,
        creditCardId: creditCard.id,
        creditCardName: creditCard.name,
        creditCardMask: creditCard.mask,
        startDate: closedCycleStart,
        endDate: closedCycleEnd,
        statementBalance: updatedCycle.statementBalance || undefined,
        minimumPayment: updatedCycle.minimumPayment || undefined,
        // nextPaymentDueDate may already be a string; normalize safely
        dueDate: creditCard.nextPaymentDueDate ? new Date(creditCard.nextPaymentDueDate as any) : undefined,
        totalSpend,
        transactioncount: nonPaymentTransactions.length,
      };
    } else {
      // Create new closed cycle
      // Adjust cycle end date to 11:59:59 PM so cycles ending "today" aren't treated as closed
      const adjustedClosedCycleEnd = adjustCycleEndToEndOfDay(closedCycleEnd);

      // Normalize due date to YYYY-MM-DD string if provided (accepts Date or string)
      const dueDateVal = creditCard.nextPaymentDueDate
        ? safeToISOString(new Date(creditCard.nextPaymentDueDate as any), 'dueDateVal conversion')?.split('T')[0]
        : null;

      const { data: newCycle, error: insertError } = await supabaseAdmin
        .from('billing_cycles')
        .insert({
          id: cycleId,
          creditCardId: creditCard.id,
          startDate: safeToISOString(closedCycleStart, 'closedCycleStart insertion')?.split('T')[0],
          endDate: safeToISOString(adjustedClosedCycleEnd, 'closedCycleEnd insertion'),
          totalSpend,
          transactioncount: nonPaymentTransactions.length,
          statementBalance: cycleStatementBalance, // Use the determined statement balance
          dueDate: dueDateVal,
          paymentstatus: 'due', // Closed cycles are typically due
          createdAt: safeToISOString(new Date(), 'createdAt field'),
          updatedAt: safeToISOString(new Date(), 'updatedAt field'),
        })
        .select()
        .single();

      if (insertError) {
        console.error('Failed to create recent closed billing cycle:', insertError);
        return null;
      }

      return {
        id: newCycle.id,
        creditCardId: creditCard.id,
        creditCardName: creditCard.name,
        creditCardMask: creditCard.mask,
        startDate: closedCycleStart,
        endDate: closedCycleEnd,
        statementBalance: newCycle.statementBalance || undefined,
        minimumPayment: newCycle.minimumPayment || undefined,
        dueDate: creditCard.nextPaymentDueDate ? new Date(creditCard.nextPaymentDueDate as any) : undefined,
        totalSpend,
        transactioncount: nonPaymentTransactions.length,
      };
    }
  } catch (error) {
    console.error('Error calculating recent closed billing cycle:', error);
    return null;
  }
}
